import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import moment from 'moment';

interface IToolContext {
    isLoading: boolean
    tool: Tool
    toolUsers: IToolUser[]
    toolMaintenances: IMaintenance[]
    selectedUserAvailable: IToolUser | null

    handleChangeToolUsers: (value: IToolUser, action: 'add' | 'edit' | 'delete') => void
    handleChangeToolMaintenances: (value: IMaintenance, action: 'add' | 'edit' | 'delete') => void
    searchUsers: (searchText: string) => void
    saveTool: (tool: Tool) => void
    openSetAvailable: (user: IToolUser) => void
    closeSetAvailable: (value: moment.Moment | null) => Promise<void>
}

export interface IUser {
    value: string
    label: string
}

export interface IMaintenance {
    maintenanceDate: moment.Moment | null
    nextMaintenanceDate: moment.Moment | null
    notes: string
}

export interface IToolUser {
    userName: string
    startDate?: moment.Moment | null
    endDate: moment.Moment | null
}

export let toolSchema = yup.object({
    toolId: yup.string().nullable().default(''),
    toolTypologyId: yup.string().required().default('').label('Tipologia'),
    serialNumber: yup.string().required().default('').label('Seriale'),
    name: yup.string().required().default('').label('Nome'),
    isAvailable: yup.boolean().default(false).label('Disponibile'),
    notes: yup.string().nullable().default('').label('Note')
});

type Tool = yup.InferType<typeof toolSchema>;

export let toolContext = React.createContext({} as IToolContext);

let { Provider } = toolContext;

let ToolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let location = useLocation();
    let history = useHistory();
    let { enqueueSnackbar } = useSnackbar();

    let isNew = location.pathname.includes("/addnew");

    let [isLoading, setLoading] = useState<boolean>(isNew === false);
    let [tool, setTool] = useState<Tool>(toolSchema.getDefault());
    let [toolUsers, setToolUsers] = useState<IToolUser[]>([]);
    let [toolMaintenances, setToolMaintenances] = useState<IMaintenance[]>([]);
    let [selectedUserAvailable, setSelectedUserAvailable] = useState<IToolUser | null>(null);


    useEffect(() => {
        if (isNew === false)
            getTool()

    }, [id, isNew]);

    let getTool = async () => {
        try {
            let res = await axios.get('/api/Tool/GetTool', { params: { toolId: id } });

            setTool(res.data.item);

            await getToolUsers();
            await getToolMaintenances();

            setLoading(false);

        } catch (err) { }
    }

    let saveTool = async (value: Tool) => {
        if (window.confirm("Vuoi salvare questo Strumento?"))
            try {
                await axios.post('/api/Tool/SaveTool', value);
                enqueueSnackbar("Tool saved", { variant: "success" });
                history.push(`/tools`);
            } catch (err) {}
    }

    let searchUsers = async (searchText: string) => {
        if (!searchText)
            return;

        try {
            const res = await axios.get('/api/User/UsersQuickSearch', { params: { searchText } });
            return res.data.items;
        } catch { }
    }


    let getToolUsers = async () => {
        try {
            let res = await axios.get('/api/Tool/GetToolUsers', { params: { toolId: id } })
            setToolUsers(res.data.items);
        } catch (err) { }
    }

    let handleChangeToolUsers = async (value: IToolUser, action: 'add' | 'edit' | 'delete') => {
        if (!value.userName || value.userName === "" || !value.startDate) {
            enqueueSnackbar("Compilare tutti i campi Utente e Data inizio correttamente", { variant: "warning" });
            return;
        }

        let oldToolUsers = [...toolUsers];

        let idx = toolUsers.findIndex(x => x.userName === value.userName && x.startDate === value.startDate);

        let ts = {
            ...value,
            startDate: moment(value.startDate).utc(true),
            endDate: moment(value.endDate).utc(true)
        }

        if (idx === -1)
            toolUsers.push(ts);
        else if (action === 'edit')
            toolUsers.splice(idx, 1, ts);
        else if (action === 'delete')
            toolUsers.splice(idx, 1);

        setToolUsers([...toolUsers]);

        try {
            let request = {
                toolUsers: [...toolUsers],
                toolId: tool.toolId
            }

            let res = await axios.post('/api/Tool/SaveToolUsers', request);

            if (res.data.affectedRecords === 1) {
                enqueueSnackbar("Assegnazioni salvate", { variant: "success" });
                await getToolUsers();
            }

        } catch (err) {
            setToolUsers([...oldToolUsers]);
        }
    }

    let getToolMaintenances = async () => {
        try {
            let res = await axios.get('/api/Tool/GetToolMaintenances', { params: { toolId: id } })
            let { item } = res.data;
            setToolMaintenances(item.maintenances);
        } catch (err) { }
    }

    let handleChangeToolMaintenances = async (value: IMaintenance, action: 'add' | 'edit' | 'delete') => {
        if (!value.maintenanceDate) {
            enqueueSnackbar("Compilare il campo Data Manutenzione correttamente", { variant: "warning" });
            return;
        }

        let oldToolMaintenances = [...toolMaintenances];

        if (action === 'add' && oldToolMaintenances.filter(f => f.maintenanceDate === value.maintenanceDate).length > 1) {
            enqueueSnackbar("Il campo Data Manutenzione deve essere univoco", { variant: "warning" });
            return;
        }

        let idx = toolMaintenances.findIndex(x => x.maintenanceDate === value.maintenanceDate);

        let mt = {
            ...value,
            maintenanceDate: moment(value.maintenanceDate).utc(true),
            nextMaintenanceDate: moment(value.nextMaintenanceDate).utc(true)
        }

        if (idx === -1)
            toolMaintenances.push(mt);
        else if (action === 'edit')
            toolMaintenances.splice(idx, 1, mt);
        else if (action === 'delete')
            toolMaintenances.splice(idx, 1);

        setToolMaintenances([...toolMaintenances]);

        try {
            let request = {
                toolMaintenances: [...toolMaintenances],
                toolId: tool.toolId
            }

            let res = await axios.post('/api/Tool/SaveToolMaintenances', request);

            if (res.data.affectedRecords === 1) {
                enqueueSnackbar("Manutenzioni salvate", { variant: "success" });
                await getToolMaintenances();
            }

        } catch (err) {
            setToolMaintenances([...oldToolMaintenances]);
        }
    }

    let openSetAvailable = (user: IToolUser) => {
        setSelectedUserAvailable({ ...user });
    }

    let closeSetAvailable = async (value: moment.Moment | null) => {
        if (value != null && selectedUserAvailable != null) {
            await handleChangeToolUsers({ ...selectedUserAvailable, endDate: value }, 'edit');
        }
        setSelectedUserAvailable(null);
    }



    return (
        <Provider
            value={{
                isLoading,
                tool,
                toolUsers,
                toolMaintenances,
                selectedUserAvailable,


                handleChangeToolUsers,
                handleChangeToolMaintenances,
                searchUsers,
                saveTool,
                openSetAvailable,
                closeSetAvailable
            }}
        >
            {children}
        </Provider>
    )
}

export default ToolProvider;